<template>
    <v-card class="ma-4">
        <v-card-title>
            <v-row>
                <v-col class="ma-2"> Per Task Measurements for {{ taskDisplayName }} </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :headers="perTaskMeasurementHeaders"
            :items="perTaskMeasurementItems"
            no-results-text="No user measurements."
            item-key="index"
            class="elevation-1"
        >
        </v-data-table>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";

import { withLoadingScreen } from "@/lib/loading";
import { getTaskDisplayName, isAttributeNameInReadoutGroup } from "@/lib/taskUtils";
import { getThingSchemaForId } from "@/lib/thingUtils";
import {
    isoDateStringToIsoStringInLocalTimeZone,
    isoDateStringToIsoStringInSiteTimeZone,
} from "@/lib/dateUtils";
import { findTaskScheduleObjects, getMeasurementDisplayValue } from "@/lib/measurementsUtils";
import { measurementsAPI } from "@/services/api.services.ts";
import { thingAPI } from "@/services/things/thingAPI.ts";

export default defineComponent({
    props: {
        dataCollectionTask: {
            type: Object,
            required: true,
        },
        taskSchedule: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            taskGroup: null,
            readoutGroup: null,
            perTaskMeasurementSets: [],
            perTaskMeasurementItems: [],
            perTaskMeasurementHeaders: [
                {
                    title: "User",
                    value: "userId",
                    align: "left",
                    sortable: true,
                    width: "10%",
                },
                {
                    title: "Attribute",
                    value: "attributeName",
                    sortable: true,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Thing",
                    value: "thingDisplayName",
                    sortable: true,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Value",
                    value: "value",
                    sortable: false,
                    align: "left",
                    width: "10%",
                },
                {
                    title: `Collection Date/Time (${this.experiment.siteTimeZone})`,
                    value: "collectionDateTime",
                    sortable: true,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Timestamp",
                    value: "timestamp",
                    sortable: true,
                    align: "left",
                    width: "10%",
                },
            ],
            showPerTaskMeasurementsForm: false,
            loadingData: true,
        };
    },

    watch: {
        dataCollectionTask: {
            handler: function () {
                return this.loadPerTaskMeasurements();
            },
            immediate: true,
        },
    },

    methods: {
        async loadPerTaskMeasurements() {
            // Clear out the existing data while we wait for the new data to load
            // Otherwise we're showing stale data while the new data is loading, and if there's
            // an error this view will be in a confusing state.
            this.loadingData = true;
            this.perTaskMeasurementSets = [];
            this.renderPerTaskMeasurementSets();

            await withLoadingScreen(async () => {
                const perTaskMeasurementSets =
                    await measurementsAPI.fetchPerTaskMeasurementSetsForDataCollectionTaskId(
                        this.dataCollectionTask.id
                    );

                const thingSchemaIdsByThingIdMap = await thingAPI.fetchThingSchemaIdsByThingIdMap(
                    this.protocol.code,
                    this.experiment.code
                );

                const { taskGroup, readoutGroup, thingSchema } = await findTaskScheduleObjects({
                    dataCollectionTask: this.dataCollectionTask,
                    experiment: this.experiment,
                    protocol: this.protocol,
                    taskSchedule: this.taskSchedule,
                });

                this.readoutGroup = readoutGroup;
                this.thingSchema = thingSchema;
                this.taskGroup = taskGroup;
                this.perTaskMeasurementSets = perTaskMeasurementSets;
                this.thingSchemaIdsByThingIdMap = thingSchemaIdsByThingIdMap;
                this.loadingData = false;
                this.renderPerTaskMeasurementSets();
            });
        },
        renderPerTaskMeasurementSets() {
            const perTaskMeasurementItems = [];
            this.perTaskMeasurementSets.forEach((perTaskMeasurementSet) => {
                const thingId = perTaskMeasurementSet.thingId;
                const thingDisplayName = perTaskMeasurementSet.thingDisplayName;
                const thingSchema = this.getThingSchemaForThingId(thingId);
                const userMeasurements = [
                    ...perTaskMeasurementSet.latestOneTimeUserMeasurements,
                    ...perTaskMeasurementSet.latestTimeSeriesUserMeasurements,
                ];

                if (thingSchema.id !== this.readoutGroup.thingSchemaId) {
                    // Currently, we only ask for measurements for values from a ReadoutGroup. However, nothing
                    // prevents measurements for things outside a ReadoutGroup, and in the future we may want
                    // to record arbitrary measurements without specifying a ReadoutGroup. In these cases, this
                    // component will need to work without a ReadoutGroup.
                    // TODO: This component won't work for "default" task measurements... not sure if it needs to
                    return;
                }

                userMeasurements.forEach((userMeasurement) => {
                    const attributeName = userMeasurement.attributeName;

                    if (
                        isAttributeNameInReadoutGroup(this.readoutGroup, thingSchema, attributeName)
                    ) {
                        const measurementValue = userMeasurement.value;
                        const displayValue = getMeasurementDisplayValue(
                            measurementValue,
                            attributeName,
                            thingSchema,
                            this.experiment,
                            this.protocol
                        );

                        const item = {
                            index: perTaskMeasurementItems.length,
                            id: perTaskMeasurementSet.id,
                            dataCollectionTaskId: perTaskMeasurementSet.dataCollectionTaskId,
                            userId: userMeasurement.userId,
                            thingSchema,
                            attributeName,
                            thingId,
                            thingDisplayName,
                            value: displayValue,
                            // display in ISO format & site time zone:
                            collectionDateTime: isoDateStringToIsoStringInSiteTimeZone(
                                userMeasurement.collectionDateTime,
                                this.experiment.siteTimeZone
                            ),
                            // display in ISO format & local time zone:
                            timestamp: isoDateStringToIsoStringInLocalTimeZone(
                                userMeasurement.timestamp
                            ),
                        };
                        perTaskMeasurementItems.push(item);
                    }
                });
            });

            this.perTaskMeasurementItems = perTaskMeasurementItems;
        },
        getThingSchemaForThingId(thingId) {
            const thingSchemaId = this.thingSchemaIdsByThingIdMap[thingId];
            return getThingSchemaForId(this.protocol, thingSchemaId);
        },
    },

    computed: {
        taskDisplayName() {
            if (this.loadingData) {
                return "";
            }

            return getTaskDisplayName(
                this.dataCollectionTask,
                this.taskGroup,
                this.readoutGroup,
                this.experiment.day0
            );
        },
    },
});
</script>
<style></style>
